import { MovieClipHelper } from "@goodgamestudios/core";

import { FrameEvent } from "../../event/FrameEvent";

import { AnimatedDisplayObject } from "./AnimatedDisplayObject";

import Container = createjs.Container;
import MovieClip = createjs.MovieClip;
import Rectangle = createjs.Rectangle;

/**
 * An AnimatedBitmap is a bitmap object that uses a sequence of images
 * from a provided bitmap to play an animation.
 *
 * @author Sascha Balkau
 * @author Dominik Willers
 * @author Jonas Hahn
 * @version 1.5
 */
export class AnimatedMovieClip extends AnimatedDisplayObject {
    constructor(loops: number = 1, id: number = 1, name: string = "") {
        super(loops, id, name);
    }

    /**
     * The movieclip containing the animation clips.
     * @private
     */
    private _mc: MovieClip;

    /**
     * An array of movieclipchildren
     * @private
     */
    private _mcs: any[] = [];

    private _loopChildren: boolean = false;

    /**
     * Returns the movieclip
     *
     * @return The movieclip containing the anim clips.
     */
    public /*override*/ get disp(): Container {
        return this._mc;
    }

		/*override*/ public get colorLayer(): any {
        if ((this.disp as any).cc)
            return (this.disp as any).cc;
        else
            return null;
    }

    public get rotation(): number {
        return this._mc.rotation;
    }

    public set rotation(value: number) {
        this._mc.rotation = value;
    }

    public get alpha(): number {
        return this._mc.alpha;
    }

    public set alpha(value: number) {
        this._mc.alpha = value;
    }

    public get scaleX(): number {
        return this._mc.scaleX;
    }

    public get scaleY(): number {
        return this._mc.scaleY;
    }

    public set scaleX(value: number) {
        this._mc.scaleX = value;
    }

    public set scaleY(value: number) {
        this._mc.scaleY = value;
    }

    public get x(): number {
        return this._mc.x;
    }

    public set x(value: number) {
        this._mc.x = value;
    }

    public get y(): number {
        return this._mc.y;
    }
    public set y(value: number) {
        this._mc.y = value;
    }
    /**
     * Processes the source movieclip.
     *
     * @param mc the source mc
     * @param m  the transformation matrix
     * @param hasAnimChild if there are movieclips within the movieclip
     */
    public /*override*/ processAnimation(mc: MovieClip, m: any /*Matrix*/ = null, hasAnimChilds: boolean = false): void {
        super.processAnimation(mc, m);
        this._hasAnimChilds = hasAnimChilds;
        this._loopChildren = false;
        this._mc = mc;
        MovieClipHelper.stopAllMovies(this._mc);

        /*
        if (m != null)
            this._mc.transform.matrix = m;
        */

        this._frameAmount = mc.totalFrames;

        if (hasAnimChilds) {
            var tempMC: MovieClip;
            var tempAMC: AnimatedMovieClip;
            for (var j: number = 0; j < mc.numChildren; j++) {
                tempMC = mc.getChildAt(j) as MovieClip;
                if (tempMC == null) continue;
                if (tempMC.totalFrames > this._frameAmount)
                    this._frameAmount = tempMC.totalFrames;
                tempMC.gotoAndStop(1);
                tempAMC = new AnimatedMovieClip(-1);
                tempAMC.processAnimation(tempMC);
                this._mcs.push(tempAMC);
            }
        }
        this.measureBounds();
        this.draw();
    }

    /**
     * Processes the source movieclip.
     *
     * @param mc the source mc
     * @param m  the transformation matrix
     * @param multiLoop you can use this if you have several Movieclips in one and want them to be wraped
     * the longest animation has to be a multiple of the main loop. hasAnimChild has no effect yet
     */
    public /*override*/ processMultiLoopAnimation(mc: MovieClip, m: any /*Matrix*/ = null): void {
        super.processMultiLoopAnimation(mc, m);
        this._id = this.id;
        this._loopChildren = true;
        this._mc = mc;
        MovieClipHelper.stopAllMovies(this._mc);

        /*
        if (m != null)
            this._mc.transform.matrix = m;
        */

        this._frameAmount = mc.totalFrames;

        var tempMC: MovieClip;
        var tempAMC: AnimatedMovieClip;

        for (var j: number = 0; j < mc.numChildren; j++) {
            if (!(mc.getChildAt(j) instanceof MovieClip)) continue;

            tempMC = mc.getChildAt(j) as MovieClip;
            if (tempMC == null) continue;

            tempAMC = new AnimatedMovieClip(-1);

            /*   				if( tempMC.numChildren > 0)			// for nested movieclips ... doesnt work yet :(
            tempAMC.processMultiLoopAnimation(tempMC,frameRate);
            else{  */

            tempAMC.processAnimation(tempMC);
            tempAMC.setFrameRate(this._frameRate);

            this._mcs.push(tempAMC);
            // }
        }

        this.measureBounds();
        this.draw();
    }

    public wrapAnimation(mc: MovieClip, startAtFrameNumber: number = 1): void {
        this._mc = mc;
        this._frameAmount = mc.totalFrames;
        this.measureBounds();
        this._frameNr = startAtFrameNumber;
        this.draw();
    }

    protected measureBounds(): void {
        var i: number = 0;
        var j: number = 0;
        var tempMC: MovieClip;

        this._bounds = this._mc.getBounds(null);
        var tmpBounds: Rectangle;
        for (i = 1; i < this._frameAmount; i++) {
            if (this._hasAnimChilds)
                for (j = 0; j < this._mc.numChildren; j++) {
                    tempMC = this._mc.getChildAt(j) as MovieClip;
                    if (tempMC != null)
                        tempMC.nextFrame();
                }
            else
                this._mc.gotoAndStop(this._mc.currentFrame + 1);
            tmpBounds = this._mc.getBounds(null);
            if (tmpBounds.left < this._bounds.left)
                this._bounds.left = tmpBounds.left;
            if (tmpBounds.top < this._bounds.top)
                this._bounds.top = tmpBounds.top;
            if (tmpBounds.right > this._bounds.right)
                this._bounds.right = tmpBounds.right;
            if (tmpBounds.bottom > this._bounds.bottom)
                this._bounds.bottom = tmpBounds.bottom;
        }

        /*
        this._bounds.width = Math.ceil(this._bounds.width * this._mc.transform.matrix.a);
        this._bounds.height = Math.ceil(this._bounds.height * this._mc.transform.matrix.d);
        this._bounds.x = Math.ceil(this._bounds.left * this._mc.transform.matrix.a);
        this._bounds.y = Math.ceil(this._bounds.top * this._mc.transform.matrix.d);
        */

        this._bounds.width = this._bounds.width;
        this._bounds.height = this._bounds.height;
        this._bounds.x = this._bounds.left;
        this._bounds.y = this._bounds.top;

        this._mc.gotoAndStop(1);
    }

		/*override*/ protected dispatchFrameEvent(type: string): void {
        this.dispatchEvent(new FrameEvent(type, this._mc.currentLabel));
    }

		/**
		 * Stops the playback of the animated movie and its children
		 */
		/*override*/ public stop(): void {
        super.stop();

        MovieClipHelper.stopAllMovies(this._mc as Container);
        if (!this._loopChildren) return;
        if (this._mcs != null) {
            for (let entryList of this._mcs) {
                var mc: any = entryList;
                if (mc === undefined) continue;

                MovieClipHelper.stopAllMovies(mc as Container);
            }
        }
    }

		/**
		 * Starts the playback of the animated movie and its children
		 */
		/*override*/ public play(): void {
        super.play();

        if (!this._loopChildren) return;
        if (this._mcs != null) {
            for (let entryList of this._mcs) {
                var mc: any = entryList;
                if (mc === undefined) continue;

                mc.play();
            }
        }
    }

		/*override*/ public playForward(): void {
        super.playForward();
        if (!this._loopChildren) return;
        if (this._mcs != null) {
            for (let entryList of this._mcs) {
                var mc: any = entryList;
                if (mc === undefined) continue;

                mc.playForward();
            }
        }
    }

    /**
     * Draws the next bitmap frame from the buffer to the animated bitmap.
     * @private
     */
    protected /*override*/ draw(): void {
        //trace("Drawing frame nr: "+_frameNr);
        var j: number = 0;
        if (this._hasAnimChilds)
            for (j = 0; j < this._mc.numChildren; j++) {
                var mc: MovieClip = this._mc.getChildAt(j) as MovieClip;
                if (mc != null)
                    mc.gotoAndStop(this._frameNr);
            }
        else
            if (this._mc.currentFrame != this._frameNr)
                this._mc.gotoAndStop(this._frameNr);
    }

    /**
     * TRANSPILER GENERATED FUNCTION: If a overriding getter is defined, the setter must be defined as well. WARNING: the defining class was not found, so the parentclass was assumed. Please check if this call is correct.
     */
    public set disp(value/*:Sprite*/)/*:void*/ {
        Object.getOwnPropertyDescriptor(AnimatedDisplayObject.prototype, "disp").set.call(this, value);
    }

    /**
     * TRANSPILER GENERATED FUNCTION: If a overriding getter is defined, the setter must be defined as well. WARNING: the defining class was not found, so the parentclass was assumed. Please check if this call is correct.
     */
    public set colorLayer(value/*:Object*/)/*:void*/ {
        Object.getOwnPropertyDescriptor(AnimatedDisplayObject.prototype, "colorLayer").set.call(this, value);
    }
}
